import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Phone } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const API_URL = "";

export const Instructors = () => {
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const response = await fetch(`${API_URL}/api/content/instructors`);
        if (response.ok) {
          setInstructors(await response.json());
        }
      } catch (err) {
        console.error('Error fetching instructors:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInstructors();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-stone-600">Loading instructors...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="instructors-page">
      <SEOHead
        title="Our Instructors"
        description="Meet the instructors at The Coppini Academy of Fine Art. Experienced artists teaching classical drawing, painting, and sculpture in San Antonio, TX."
        canonicalPath="/classes/instructors"
      />
      {/* Hero */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif text-stone-900 mb-6">Our Instructors</h1>
          <p className="text-xl text-stone-600">
            Learn from experienced artists dedicated to classical and representational fine art
          </p>
          <p className="text-stone-500 mt-4">
            <Link to="/classes/adult" className="text-cafa-red hover:text-cafa-red-900 underline mr-4">
              Adult Classes →
            </Link>
            <Link to="/classes/kids" className="text-cafa-red hover:text-cafa-red-900 underline">
              Kids Classes →
            </Link>
          </p>
        </div>
      </section>

      {/* Instructors Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {instructors.length === 0 ? (
            <div className="text-center text-stone-600 py-12">
              <p>No instructors found. Please check back later!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {instructors.filter(i => i.name !== 'Susie Short').map((instructor) => (
                <Card key={instructor.id} className="border-stone-200 overflow-hidden hover:shadow-lg transition-shadow" data-testid={`instructor-card-${instructor.id}`}>
                  <img
                    src={instructor.imageUrl}
                    alt={instructor.name}
                    className="w-full h-64 object-cover"
                  />
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-stone-900 mb-2">{instructor.name}</h3>
                    <p className="text-sm text-cafa-red font-medium mb-3">{instructor.specialty}</p>
                    <p className="text-sm text-stone-600 leading-relaxed">{instructor.bio}</p>
                    {instructor.phone && (
                      <a
                        href={`tel:${instructor.phone.replace(/\D/g, '')}`}
                        className="inline-flex items-center justify-center mt-4 px-5 py-2.5 bg-cafa-red hover:bg-cafa-red-900 text-white text-sm font-medium rounded-md transition-colors"
                        data-testid={`instructor-call-${instructor.id}`}
                      >
                        <Phone size={16} className="mr-2" />
                        Call {instructor.phone}
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif text-stone-900 mb-4">Ready to Start Learning?</h2>
          <p className="text-stone-600 mb-8">
            Explore our class offerings and find the perfect fit for your artistic journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/classes/adult"
              className="inline-flex items-center justify-center px-6 py-3 bg-cafa-red hover:bg-cafa-red-900 text-white rounded-md font-medium transition-colors"
            >
              View Adult Classes
            </Link>
            <Link 
              to="/classes/kids"
              className="inline-flex items-center justify-center px-6 py-3 border border-cafa-red text-cafa-red hover:bg-cafa-red-50 rounded-md font-medium transition-colors"
            >
              View Kids Classes
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
